/* ------------------------------------------------------------------
   Fabric PDP — Set Builder (standard-builder engine, Phase 7.3
   mobile-first UX refinement).

   STANDARD BUILDER ENGINE:
   - Gender -> real Shopify variant mapping, catalog accessors.
   - Multi-item `/cart/add.js` submission (theme.routes.cartAdd,
     `_set_id` + `_person` private properties, native cart-drawer
     refresh events).
   - Pricing-preview math (integer cents, 1/2/3+ -> 0%/10%/15% tiers).
   - Size: Gender metaobject's sizes.value, in original order,
     rendered as a compact <select> (see populateSelect()). The
     metaobject's CUSTOM entry is excluded entirely by the Liquid
     template (snippets/fabric-product-options.liquid) -- legacy
     Custom-as-Size routing has been removed from Fabric, so every Size
     in this list is a real, standard, selectable value.
   - Style: active, gender-specific Style Master entries resolved through
     the active Style Guide by PTStyleCatalog; required for every set,
     including Custom Size. The select stores Style Code.
   - Gender remains chip-style (buildPillGroup()); Size/Style use the
     smaller populateSelect() -- both unchanged from Phase 7.2/7.3.
   - formatMoney()/slugify() are unchanged from Phase 7.2.

   PHASE 7.3 ADDITIONS:
   - MAX_SETS reduced to 6 for Fabric (data.maxSets, defaulted here to
     6 rather than 8).
   - An explicit per-set lifecycle, held in state rather than inferred
     from the DOM: every set object now also carries `confirmed`
     (boolean), `expanded` (boolean), `expandStamp` (ordering for the
     2-card expand cap), `pendingGenderChange` (set only while the
     Gender-change confirmation dialog is open) and `pendingRemove`
     (armed a "tap again to remove" state for an already-confirmed
     set). Reopening a confirmed set (expanding it again) never resets
     any of gender/size/style — only the three field setters below can
     change those, and only a *real* change to one of them clears
     `confirmed`.
   - computeProgress()/computePricing()/renderSummary() now key off
     `confirmed`, not merely `complete`: the pricing preview, the
     summary count ("N of M sets confirmed") and the final Add to Cart
     gate all require every active set to be explicitly confirmed, not
     just technically complete.
   - setStyle() routes through refreshLight() rather than the full
     render() pipeline, so the rest of a set card's DOM is never
     rebuilt on a Style click. A pill's checked/unchecked visual state
     is already handled natively by the browser (the click sets
     `checked` before any JS runs) plus the existing
     `:checked + .fabric-builder__pill-label` CSS rule, so no DOM
     rebuild is actually needed for the visual selection itself.
   - A lightweight, non-native (no `alert()`) inline confirmation is
     shown before a Gender change that would discard meaningful
     existing Size/Style data on a set — see
     requestGenderChange()/pendingGenderChange. Only shown when there
     is genuinely something to lose; the underlying reset-on-Gender-
     change logic itself (setGender()) is unchanged from Phase 7.2.
   - Accordion discipline: normally one set expanded at a time, hard
     cap of two simultaneously expanded (expandSet()); "+ Add Another
     Set" always collapses every other set; confirming a set collapses
     it. Expanding/collapsing a card is a pure UI toggle and never
     touches gender/size/style.
   - Any-position Remove is preserved for every set (not Matching's
     LIFO-only pattern). Removing an already-CONFIRMED set is
     optionally guarded by a lightweight "tap again to remove" arm
     (pendingRemove) that auto-disarms after a few seconds, so a stray
     tap on a de-emphasized Remove control on a collapsed high-value
     card cannot silently delete it.

   Bound only to [data-fabric-builder] root(s). Does not touch global
   product forms, the native variant-picker, or any other section.

   PAID CUSTOMISATION SERVICE:
   - A set can now also be "Customised" instead of Standard --
     `set.customisation` (a draft object owned by, and validated by,
     the shared popup module assets/pt-customisation-popup.js) is
     truthy exactly when it is. See isCustomised()/buildSet().
   - The legacy Custom-as-Size path (CUSTOM_SIZE_VALUE, setSize()'s
     Custom-boundary branch, the disabled [data-fabric-custom-note]
     CTA) has been removed now that this real Customisation service is
     live. Custom Size replaces standard size completion only; Style
     remains required through the regular builder control.
   - Saving Custom Size preserves standard size for reversal and the
     regular Style Code for both sizing paths. Style remains editable.
   - A Gender change on an already-Customised set invalidates it
     (invalidateCustomisationForGenderChange()) rather than silently
     carrying Gender-mismatched Style/Size data across -- routed
     through the same existing pendingGenderChange confirmation dialog
     used for Standard sets (hasMeaningfulData() now also considers
     `customisation`).
   - handleAddToCart() submits one extra cart line per Customised set
     (the real Shopify product configured in Theme Settings -- see
     PTCustomisation.getProductInfo() -- never a client-invented
     price/variant), identified by its own private `_customisation_id`
     (never `_set_id`/`_person`, per the task's explicit instruction),
     so two Customised profiles with identical visible selections never
     merge into one cart line.
   - No change to computePricing()/renderPricing(): the PDP's estimated garment total remains garment-price-only, exactly
     as before. The ₹500 Customisation charge appears where every other
     service/add-on product already does -- as its own line in the real
     Shopify cart -- rather than being folded into a second, parallel
     pricing calculation here.

------------------------------------------------------------------- */
(function () {
  'use strict';

  var GENDER_ORDER = ['WOMEN', 'MEN', 'GIRLS', 'BOYS'];
  var GENDER_TEXT = {
    WOMEN: 'Women',
    MEN: 'Men',
    GIRLS: 'Girls',
    BOYS: 'Boys'
  };

  // Note: the legacy CUSTOM_SIZE_VALUE routing constant has been
  // removed along with Custom-as-Size -- snippets/fabric-product-
  // options.liquid's *_sizes_json captures now exclude the metaobject's
  // CUSTOM entry entirely, so the Fabric Size <select> never offers a
  // "Custom" value.
  var REMOVE_ARM_TIMEOUT_MS = 4000;

  /* ---- Shopify-standard money formatting — copied verbatim from
     assets/matching-set-builder.js (same shop money_format, same
     placeholder syntax, no global Shopify.formatMoney relied on). */
  function formatMoney(cents, format) {
    if (typeof cents !== 'number' || isNaN(cents)) cents = 0;
    format = format || '{{amount}}';

    function withDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'number' ? precision : 2;
      thousands = thousands || ',';
      decimal = decimal || '.';
      var value = (number / 100).toFixed(precision);
      var parts = value.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var fraction = parts[1] ? decimal + parts[1] : '';
      return dollars + fraction;
    }

    var placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var placeholderKey = placeholderMatch ? placeholderMatch[1] : 'amount';
    var formatted;

    switch (placeholderKey) {
      case 'amount_no_decimals':
        formatted = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        formatted = withDelimiters(cents, 2);
    }

    return placeholderMatch ? format.replace(placeholderMatch[0], formatted) : formatted;
  }

  function slugify(value) {
    return String(value).replace(/[^a-zA-Z0-9]+/g, '-');
  }

  function init(root) {
    if (root.dataset.fabricInitialized === 'true') return;
    root.dataset.fabricInitialized = 'true';

    var instanceId = root.getAttribute('data-instance-id') || '';
    var dataScript = document.getElementById('fabric-builder-data-' + instanceId);
    if (!dataScript) return;

    var data;
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (err) {
      return;
    }

    var MAX_SETS = data.maxSets || 6;
    var MIN_SETS = data.minSets || 1;

    var els = {
      catalogError: root.querySelector('[data-fabric-catalog-error]'),
      setsContainer: root.querySelector('[data-fabric-sets]'),
      setTemplate: root.querySelector('[data-fabric-set-template]'),
      addSetBtn: root.querySelector('[data-fabric-add-set]'),
      summary: root.querySelector('[data-fabric-summary]'),
      summaryMessage: root.querySelector('[data-fabric-summary-message]'),
      summaryCount: root.querySelector('[data-fabric-summary-count]'),
      pricing: root.querySelector('[data-fabric-pricing]'),
      subtotal: root.querySelector('[data-fabric-subtotal]'),
      setBenefitRow: root.querySelector('[data-fabric-set-benefit-row]'),
      setBenefit: root.querySelector('[data-fabric-set-benefit]'),
      benefitLabel: root.querySelector('[data-fabric-benefit-label]'),
      finalPrice: root.querySelector('[data-fabric-final-price]'),
      pricingDetail: root.querySelector('[data-fabric-pricing-detail]'),
      totalLabel: root.querySelector('[data-fabric-total-label]'),
      benefitNote: root.querySelector('[data-fabric-benefit-note]'),
      cartError: root.querySelector('[data-fabric-cart-error]'),
      cta: root.querySelector('[data-fabric-add-to-cart]')
    };

    if (!els.setsContainer || !els.setTemplate) return;

    var setCounter = 0;
    var expandStampCounter = 0;
    var state = {
      sets: [],
      submitting: false
    };

    var editMode = new URLSearchParams(window.location.search).has('edit_set');
    var editSession = null;
    var editBlocked = editMode;

    // Meta Pixel funnel events (ProductEngaged / ProductConfigured).
    // See assets/pdp-funnel-tracking.js for the shared fbq wrapper.
    // Local to this init() call -- resets per page load, never
    // persists across navigations. Additive observers only: never
    // touch PDP state, validation, the cart, or the Add to Cart
    // button.
    var funnelEngagedFired = false;
    var funnelConfiguredFired = false;

    function funnelTrackingParams(extra) {
      var params = {
        content_id: data.productId || undefined,
        content_name: data.productTitle || undefined,
        product_type: data.productType || undefined,
        currency: data.currencyCode || undefined
      };
      if (extra) {
        for (var key in extra) {
          if (Object.prototype.hasOwnProperty.call(extra, key)) params[key] = extra[key];
        }
      }
      return params;
    }

    /* ---------------------------------------------------------------
       Catalog data accessors — unchanged from Phase 7.2.
    ------------------------------------------------------------------ */
    function genderReady(gender) {
      return !!(data.genderReady && data.genderReady[gender]);
    }

    function sizesForGender(gender) {
      var entry = data.sizesByGender && data.sizesByGender[gender];
      var chartSizes = window.PTSizeGuide && window.PTSizeGuide.standardSizes(gender);
      if (chartSizes) return chartSizes;
      if (!entry || !entry.available || !entry.sizes || !entry.sizes.length) return null;
      return entry.sizes;
    }

    function priceForGender(gender) {
      var entry = data.genderVariants && data.genderVariants[gender];
      if (!entry || !entry.available) return null;
      return entry.priceCents;
    }

    function variantIdForGender(gender) {
      var entry = data.genderVariants && data.genderVariants[gender];
      if (!entry || !entry.available) return null;
      return entry.variantId;
    }

    /* ---------------------------------------------------------------
       Set state — one flat list (no relationship groups). Every set
       is independently added/removed by the customer; labels are
       positional ("Set N") and renumber when a set is removed.

       Phase 7.3 adds the lifecycle fields documented in the file
       header: confirmed / expanded / expandStamp / pendingGenderChange
       / pendingRemove / removeArmTimer.
    ------------------------------------------------------------------ */
    function nextSetId() {
      setCounter += 1;
      return 'fabric-set-' + instanceId + '-' + setCounter;
    }

    function buildSet() {
      return {
        id: nextSetId(),
        gender: null,
        size: null,
        style: null,
        confirmed: false,
        expanded: true,
        expandStamp: 0,
        pendingGenderChange: null,
        pendingRemove: false,
        removeArmTimer: null,
        // Paid Customisation service (assets/pt-customisation-popup.js).
        // `customisation` is the saved draft (see that file's
        // currentDraft() for its shape) once the customer has
        // explicitly clicked "CUSTOMISE THIS SET" -- its mere presence
        // is what makes a set "Customised" instead of "Standard"; see
        // isCustomised()/setCompletionInfo(). Standard `size`/`style`
        // are deliberately NOT cleared when this is set (see
        // saveCustomisation()) so "Use standard options instead" can
        // restore them exactly. `customisationDraftCarryover` holds the
        // Gender-independent fields (name/measurements/notes) preserved
        // across a Gender change that invalidated an active
        // Customisation -- see invalidateCustomisationForGenderChange().
        customisation: null,
        customisationDraftCarryover: null
      };
    }

    function findSet(id) {
      for (var i = 0; i < state.sets.length; i++) {
        if (state.sets[i].id === id) return state.sets[i];
      }
      return null;
    }

    function clearRemoveArm(set) {
      if (set.removeArmTimer) {
        clearTimeout(set.removeArmTimer);
        set.removeArmTimer = null;
      }
      set.pendingRemove = false;
    }

    // "+ Add Another Set" always opens the new set expanded and
    // collapses every other set (Phase 7.3 instruction: "opens a
    // fresh blank set while collapsing others"), regardless of
    // whether those other sets are complete/confirmed yet.
    function addSet() {
      if (state.sets.length >= MAX_SETS) return;
      state.sets.forEach(function (s) { s.expanded = false; });
      var set = buildSet();
      expandStampCounter += 1;
      set.expandStamp = expandStampCounter;
      state.sets.push(set);
      render();
      scrollSetIntoView(set.id);
    }

    function removeSet(id) {
      if (state.sets.length <= MIN_SETS) return;
      var idx = -1;
      for (var i = 0; i < state.sets.length; i++) {
        if (state.sets[i].id === id) { idx = i; break; }
      }
      if (idx === -1) return;
      clearRemoveArm(state.sets[idx]);
      state.sets.splice(idx, 1);
      render();
    }

    // Expands `setId`. With collapseOthers=true every other set is
    // collapsed (used by "+ Add Another Set"). Otherwise the normal
    // accordion rule applies: at most one *other* set stays expanded
    // alongside this one (hard cap of two simultaneously expanded) --
    // the oldest-expanded excess is collapsed.
    function expandSet(setId, collapseOthers) {
      var set = findSet(setId);
      if (!set) return;

      if (collapseOthers) {
        state.sets.forEach(function (s) {
          if (s.id !== setId) s.expanded = false;
        });
      } else {
        var others = state.sets.filter(function (s) {
          return s.id !== setId && s.expanded;
        });
        others.sort(function (a, b) {
          return (a.expandStamp || 0) - (b.expandStamp || 0);
        });
        while (others.length > 1) {
          others.shift().expanded = false;
        }
      }

      set.expanded = true;
      expandStampCounter += 1;
      set.expandStamp = expandStampCounter;
    }

    // Gender change resets Size AND Style -- both are standard,
    // Gender-scoped selections, so a stale Size/Style from the
    // previous Gender must never silently carry over into the new
    // Gender's cart line. A real change also clears `confirmed` --
    // reopening/expanding a set on its own never does.
    function setGender(setId, gender) {
      var set = findSet(setId);
      if (!set) return;
      if (set.gender === gender) return;
      set.gender = gender;
      set.size = null;
      set.style = null;
      invalidateCustomisationForGenderChange(set);
      if (set.confirmed) set.confirmed = false;

      if (!funnelEngagedFired && window.PTFunnelTracking) {
        var funnelEngagedAccepted = window.PTFunnelTracking.fire('ProductEngaged', funnelTrackingParams({
          selected_gender: gender
        }));
        // Only latch the guard once the helper has actually taken
        // ownership of the event (sent, or safely queued) -- never
        // on a discard, so a later real interaction gets a fair
        // retry.
        if (funnelEngagedAccepted) funnelEngagedFired = true;
      }

      render();
    }

    function hasMeaningfulData(set) {
      return !!(set.size || set.style || set.customisation);
    }

    // Whether a set is currently in Customised mode. Deliberately a
    // one-line predicate (not inlined at each call site) so every
    // place that branches on Standard-vs-Customised reads the same
    // way -- see setCompletionInfo(), buildCollapsedSummaryText(),
    // buildSetCard(), handleAddToCart().
    function isCustomised(set) {
      return !!set.customisation;
    }

    // Called from setGender() whenever a Gender change actually
    // applies. A saved Customisation is Gender-scoped (its Top/Bottom Size
    // values come from that Gender's own catalogue -- assets/pt-
    // customisation-popup.js), so it must not silently remain "valid"
    // under a different Gender. Gender-independent fields (name,
    // measurements, notes) are worth keeping -- stashed as
    // `customisationDraftCarryover` so they resurface next time the
    // popup opens for this set -- but Top Size/Bottom Size are
    // dropped, and the set stops counting as Customised until the
    // customer explicitly reviews and re-saves it (see setCompletionInfo()).
    function invalidateCustomisationForGenderChange(set) {
      if (!set.customisation) return;
      var prev = set.customisation;
      set.customisationDraftCarryover = {
        profileName: prev.profileName,
        chest: prev.chest,
        waist: prev.waist,
        hips: prev.hips,
        height: prev.height,
        notes: prev.notes,
        measurementsLater: prev.measurementsLater
      };
      set.customisation = null;
    }

    // Draft to pre-fill when opening the popup for this set: the saved
    // Customisation if one exists (editing), otherwise a Gender-change
    // carryover if one exists (see above), otherwise nothing.
    function draftForPopup(set) {
      return set.customisation || set.customisationDraftCarryover || null;
    }

    // Gender changes on a set that already has Size/Style/measurement
    // data worth losing are routed through a lightweight confirmation
    // instead of applying immediately -- see the file header and
    // [data-fabric-gender-confirm] in the template. A Gender chosen on
    // an otherwise-empty set (nothing to lose) applies immediately, as
    // in Phase 7.2.
    function requestGenderChange(setId, gender) {
      var set = findSet(setId);
      if (!set) return;
      if (set.gender === gender) return;
      if (set.gender && hasMeaningfulData(set)) {
        set.pendingGenderChange = gender;
        render();
        return;
      }
      setGender(setId, gender);
    }

    function confirmGenderChange(setId) {
      var set = findSet(setId);
      if (!set || !set.pendingGenderChange) return;
      var gender = set.pendingGenderChange;
      set.pendingGenderChange = null;
      setGender(setId, gender);
    }

    function cancelGenderChange(setId) {
      var set = findSet(setId);
      if (!set) return;
      set.pendingGenderChange = null;
      render();
    }

    /* ---------------------------------------------------------------
       Paid Customisation service -- assets/pt-customisation-popup.js
       owns the popup itself (form fields, validation, the Gender-
       specific size catalogue); this file owns only the two
       transitions below, exactly as the task's reusability boundary
       specifies. Opening the popup (see onClick()'s [data-fabric-
       customise-cta] branch) never calls either of these -- only an
       explicit save from inside the popup (onSave) or an explicit
       "Use standard options instead" click does.
    ------------------------------------------------------------------ */
    // Called from PTCustomisation's onSave callback once the customer
    // has explicitly clicked "CUSTOMISE THIS SET". Standard size/style
    // are deliberately left untouched (see the file header and
    // buildSet()) so a later useStandardOptions() call restores them
    // exactly as they were.
    function saveCustomisation(setId, draft) {
      var set = findSet(setId);
      if (!set) return;
      set.customisation = draft;
      set.customisationDraftCarryover = null;
      if (set.confirmed) set.confirmed = false;
      render();
    }

    // "Use standard options instead" (Phase: Customisation reversal).
    // Only clears `customisation`, restoring the retained standard size
    // while keeping the current regular Style selection.
    function useStandardOptions(setId) {
      var set = findSet(setId);
      if (!set) return;
      if (!set.customisation) return;
      set.customisation = null;
      if (set.confirmed) set.confirmed = false;
      render();
    }

    // Every Size value is now a real, standard one (legacy Custom-as-
    // Size has been removed) -- a Size change never needs to clear
    // Style. A real change to Size still clears `confirmed`.
    function setSize(setId, size) {
      var set = findSet(setId);
      if (!set) return;
      var next = size || null;
      if (set.size === next) return;
      set.size = next;
      if (set.confirmed) set.confirmed = false;
      render();
    }

    // Style selection routes through refreshLight(), never the full
    // render(), so the rest of the set card's DOM is not rebuilt on
    // every click. See the file header for the full explanation.
    function setStyle(setId, style) {
      var set = findSet(setId);
      if (!set) return;
      var next = window.PTStyleCatalog.find(set.gender, style) ? style : null;
      if (set.style === next) return;
      set.style = next;
      if (set.confirmed) set.confirmed = false;
      refreshLight(setId);
    }

    function confirmSet(setId) {
      var set = findSet(setId);
      if (!set) return;
      var info = setCompletionInfo(set);
      if (!info.complete) return;
      set.confirmed = true;
      set.expanded = false;
      render();
    }

    /* ---------------------------------------------------------------
       Completion / validation. Every active set needs Gender + Style
       and either standard Size or a saved Custom Size draft. "Complete" only gates whether a set *can* be confirmed --
       cart-readiness and pricing are gated on `confirmed` (see
       computeProgress()/computePricing() below), which is the Phase
       7.3 change from Phase 7.2's completion-only gate.
    ------------------------------------------------------------------ */
    function setCompletionInfo(set) {
      var gender = set.gender;
      var genderChosen = !!set.gender;
      var genderResolved = genderChosen && genderReady(set.gender);
      var blockedByCatalog = genderChosen && !genderReady(set.gender);

      var styleValid = !!window.PTStyleCatalog.find(gender, set.style);
      var stylesAvailable = window.PTStyleCatalog.options(gender).length > 0;
      blockedByCatalog = blockedByCatalog || (genderResolved && !stylesAvailable);
      // Custom Size replaces standard size only; the regular Style is required.
      if (isCustomised(set)) {
        var customisedMissing = genderResolved ? [] : ['Gender'];
        if (!styleValid) customisedMissing.push('Style');
        return { complete: genderResolved && styleValid, missing: customisedMissing, blockedByCatalog: blockedByCatalog };
      }

      var sizeList = genderResolved ? sizesForGender(set.gender) : null;
      var sizeDataOk = genderResolved && !!sizeList;
      var styleDataOk = genderResolved && window.PTStyleCatalog.options(gender).length > 0;

      var missing = [];
      if (!set.gender) missing.push('a Gender');
      if (!set.size) missing.push('Size');
      if (!styleValid) missing.push('Style');

      var complete = genderResolved && sizeDataOk && !!set.size && styleDataOk && styleValid;

      return { complete: complete, missing: missing, blockedByCatalog: blockedByCatalog };
    }

    function computeProgress() {
      var total = state.sets.length;
      var completeCount = 0;
      var confirmedCount = 0;
      var anyBlocked = false;
      var incompleteSets = [];
      var unconfirmedCompleteSets = [];

      state.sets.forEach(function (set, index) {
        var info = setCompletionInfo(set);
        if (info.complete) {
          completeCount++;
          if (set.confirmed) {
            confirmedCount++;
          } else {
            unconfirmedCompleteSets.push({ set: set, index: index, info: info });
          }
        } else {
          incompleteSets.push({ set: set, index: index, info: info });
        }
        if (info.blockedByCatalog) anyBlocked = true;
      });

      return {
        total: total,
        completeCount: completeCount,
        confirmedCount: confirmedCount,
        incompleteSets: incompleteSets,
        unconfirmedCompleteSets: unconfirmedCompleteSets,
        anyBlocked: anyBlocked
      };
    }

    /* ---------------------------------------------------------------
       Pricing / savings preview -- Matching's final computePricing(),
       unchanged in principle: integer cents throughout, 1/2/3+ ->
       0%/10%/15% tiers (unchanged by the 8->6 max-set reduction, since
       the tier rule was already "3 or more" not "3 to 8"), Shopify's
       own automatic discount remains the sole authority at checkout.

       Phase 7.3 change: only *confirmed* sets are counted here, not
       merely complete ones -- the pricing preview reflects what the
       customer has actually locked in, not what they happen to have
       filled in on a still-open card.
    ------------------------------------------------------------------ */
    function computePricing() {
      var pricedPeople = state.sets.filter(function (set) {
        return set.confirmed;
      });
      var count = pricedPeople.length;
      var subtotalCents = pricedPeople.reduce(function (total, set) {
        return total + (priceForGender(set.gender) || 0);
      }, 0);

      // PDP-only estimate. Existing cart items are not inferred; paid services are added after the garment benefit.
      var tierPercent = count >= 3 ? 15 : (count === 2 ? 10 : 0);
      var setBenefitCents = Math.round((subtotalCents * tierPercent) / 100);
      var serviceInfo = window.PTCustomisation ? window.PTCustomisation.getProductInfo() : null;
      var serviceCount = pricedPeople.filter(function (person) { return !!person.customisation; }).length;
      var serviceCents = serviceInfo && serviceInfo.available ? serviceCount * serviceInfo.priceCents : 0;
      return {
        count: count,
        subtotalCents: subtotalCents,
        serviceCents: serviceCents,
        tierPercent: tierPercent,
        setBenefitCents: setBenefitCents,
        finalPriceCents: subtotalCents - setBenefitCents + serviceCents
      };
    }

    function summaryMessageText(progress) {
      if (state.allComplete) return 'Your selections are ready';
      if (progress.anyBlocked) return 'Some options are unavailable — review your selections to continue';
      if (!state.sets.some(function (item) { return item.size || item.style || item.customisation; })) {
        return 'Select size & style to continue';
      }
      if (progress.completeCount === progress.total) {
        return 'Confirm each person to continue';
      }
      return 'Complete size & style for all selected members';
    }

    /* ---------------------------------------------------------------
       Collapsed-card order-review line. The Gender segment lives on
       the set label itself (see buildSetCard() below: "Set 1 ·
       Women"), so this line covers only Size and Style.
    ------------------------------------------------------------------ */
    function buildCollapsedSummaryText(set) {
      if (!set.gender) return 'Not started yet';
      if (isCustomised(set)) {
        var custPriceInfo = window.PTCustomisation ? window.PTCustomisation.getProductInfo() : null;
        return ((custPriceInfo && custPriceInfo.priceFormatted) ? ('Customised · ' + custPriceInfo.priceFormatted) : 'Customised')
          + '\n' + (window.PTStyleCatalog.label(set.gender, set.style) || 'Style not chosen');
      }
      if (!set.size) return 'Size not chosen';
      var head = window.PTSizeGuide ? window.PTSizeGuide.summary(set.gender, set.size) : set.size;
      head += '\n' + (set.style ? window.PTStyleCatalog.label(set.gender, set.style) : 'Style not chosen');
      return head;
    }

    /* ---------------------------------------------------------------
       Rendering.
    ------------------------------------------------------------------ */
    function buildPillGroup(container, config) {
      container.innerHTML = '';
      var anyEnabled = false;

      config.options.forEach(function (opt) {
        if (!opt.disabled) anyEnabled = true;

        var wrap = document.createElement('div');
        wrap.className = 'fabric-builder__pill-option';

        var inputId = 'fabric-' + config.field + '-' + config.setId + '-' + slugify(opt.value);

        var input = document.createElement('input');
        input.type = 'radio';
        input.className = 'fabric-builder__pill-input';
        input.name = config.name;
        input.id = inputId;
        input.value = opt.value;
        input.disabled = !!opt.disabled;
        input.checked = !!opt.selected;
        input.setAttribute('data-fabric-field', config.field);
        input.setAttribute('data-set-id', config.setId);

        var label = document.createElement('label');
        label.className = 'fabric-builder__pill-label';
        label.setAttribute('for', inputId);
        label.textContent = opt.text + (opt.disabled ? ' (unavailable)' : '');

        wrap.appendChild(input);
        wrap.appendChild(label);
        container.appendChild(wrap);
      });

      return anyEnabled;
    }

    // Populates a native <select> for a Standard-builder field (Size
    // or Style). A native select needs no per-option wrapper markup
    // and already provides its own keyboard/touch affordances, so
    // this is intentionally a separate, smaller function rather than
    // a buildPillGroup() variant. The leading placeholder option is
    // disabled/hidden and only ever shown when nothing is selected
    // yet -- it is never a real, selectable value.
    function populateSelect(selectEl, config) {
      selectEl.innerHTML = '';
      var anyEnabled = false;

      var placeholderOpt = document.createElement('option');
      placeholderOpt.value = '';
      placeholderOpt.textContent = config.placeholder;
      placeholderOpt.disabled = true;
      placeholderOpt.hidden = true;
      selectEl.appendChild(placeholderOpt);

      var hasSelection = false;
      config.options.forEach(function (opt) {
        if (!opt.disabled) anyEnabled = true;
        var optionEl = document.createElement('option');
        optionEl.value = opt.value;
        optionEl.textContent = opt.text + (opt.disabled ? ' (unavailable)' : '');
        optionEl.disabled = !!opt.disabled;
        if (opt.selected) {
          optionEl.selected = true;
          hasSelection = true;
        }
        selectEl.appendChild(optionEl);
      });

      placeholderOpt.selected = !hasSelection;
      selectEl.disabled = !anyEnabled;
      return anyEnabled;
    }

    function buildSetCard(set, index, removable) {
      var node = els.setTemplate.content.firstElementChild.cloneNode(true);
      node.setAttribute('data-set-id', set.id);
      ['size', 'style'].forEach(function (field) {
        var select = node.querySelector('[data-fabric-' + field + '-select]');
        var label = node.querySelector('[data-pt-field-label="' + field + '"]');
        if (select && label) {
          select.id = set.id + '-' + field;
          label.htmlFor = select.id;
        }
      });
      var styleGuide = node.querySelector('[data-pt-style-guide]');
      if (styleGuide) {
        styleGuide.dataset.ptStyleGuide = set.gender || '';
        styleGuide.hidden = !set.gender;
      }
      var sizeGuide = node.querySelector('[data-pt-size-guide]');
      if (sizeGuide) {
        sizeGuide.dataset.ptSizeGuide = set.gender || '';
        sizeGuide.hidden = !set.gender;
      }

      var labelEl = node.querySelector('[data-fabric-set-label]');
      if (labelEl) {
        // F2.1: gender suffix now shows only once collapsed/confirmed,
        // matching Matching's personHeadingText() pattern -- an
        // expanded card stays a plain "Set N" label, and the suffix
        // reappears (with the current gender) once the card collapses
        // again, whatever gender is selected at that time.
        var labelText = 'Person ' + (index + 1);
        if (set.gender && !set.expanded) labelText += ' · ' + GENDER_TEXT[set.gender];
        labelEl.textContent = labelText;
      }

      var info = setCompletionInfo(set);
      node.classList.toggle('fabric-builder__set--complete', info.complete);
      node.classList.toggle('fabric-builder__set--confirmed', !!set.confirmed);
      node.classList.toggle('fabric-builder__set--collapsed', !set.expanded);

      var errorEl = node.querySelector('[data-fabric-set-error]');
      if (errorEl) {
        if (info.blockedByCatalog) {
          errorEl.textContent = 'Unavailable';
          errorEl.hidden = false;
        } else if (!info.complete && set.editNotice) {
          errorEl.textContent = set.editNotice;
          errorEl.hidden = false;
        } else {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }
      }

      // Collapsed-only order-review line + Edit/Continue affordance.
      var summaryLineEl = node.querySelector('[data-fabric-set-summary-line]');
      var editHintEl = node.querySelector('[data-fabric-set-edit-hint]');
      if (!set.expanded) {
        if (summaryLineEl) {
          summaryLineEl.hidden = false;
          summaryLineEl.textContent = buildCollapsedSummaryText(set);
        }
        if (editHintEl) {
          editHintEl.hidden = false;
          editHintEl.textContent = set.confirmed ? 'Edit' : 'Continue';
        }
      } else {
        if (summaryLineEl) { summaryLineEl.hidden = true; summaryLineEl.textContent = ''; }
        if (editHintEl) editHintEl.hidden = true;
      }

      var bodyEl = node.querySelector('[data-fabric-set-body]');
      if (bodyEl) bodyEl.hidden = !set.expanded;

      var removeBtn = node.querySelector('[data-fabric-set-remove]');
      if (removeBtn) {
        removeBtn.hidden = !removable;
        if (set.pendingRemove) {
          removeBtn.textContent = 'Tap to confirm remove';
          removeBtn.classList.add('fabric-builder__set-remove--armed');
        } else {
          removeBtn.textContent = 'Remove';
          removeBtn.classList.remove('fabric-builder__set-remove--armed');
        }
        if (removable) {
          removeBtn.setAttribute('aria-label', (set.pendingRemove ? 'Confirm remove Person ' : 'Remove Person ') + (index + 1));
        }
      }

      var genderOptions = node.querySelector('[data-fabric-gender-options]');
      if (genderOptions) {
        buildPillGroup(genderOptions, {
          field: 'gender',
          setId: set.id,
          name: 'fabric-gender-' + set.id,
          options: GENDER_ORDER.map(function (g) {
            return {
              value: g,
              text: GENDER_TEXT[g],
              selected: set.gender === g,
              disabled: !genderReady(g)
            };
          })
        });
      }

      var genderConfirmEl = node.querySelector('[data-fabric-gender-confirm]');
      if (genderConfirmEl) {
        if (set.pendingGenderChange) {
          genderConfirmEl.hidden = false;
          var genderConfirmTextEl = genderConfirmEl.querySelector('[data-fabric-gender-confirm-text]');
          if (genderConfirmTextEl) {
            var genderConfirmFields = set.customisation ? 'Size, Style and your saved Customisation' : 'Size and Style';
            genderConfirmTextEl.textContent = 'Change who this set is for? Switching to '
              + GENDER_TEXT[set.pendingGenderChange]
              + ' will clear this set’s ' + genderConfirmFields + '.';
          }
        } else {
          genderConfirmEl.hidden = true;
        }
      }

      var sizeSelect = node.querySelector('[data-fabric-size-select]');
      if (sizeSelect) {
        sizeSelect.setAttribute('data-set-id', set.id);
        var sizes = set.gender ? sizesForGender(set.gender) : null;
        if (sizes) {
          populateSelect(sizeSelect, {
            placeholder: 'Choose a Size',
            options: sizes.map(function (s) {
              return { value: s, text: window.PTSizeGuide ? window.PTSizeGuide.optionLabel(set.gender, s) : s, selected: set.size === s };
            })
          });
        } else {
          populateSelect(sizeSelect, {
            placeholder: set.gender ? 'Size data unavailable' : 'Select',
            options: []
          });
        }
      }

      // Custom Size hides only standard size; Style stays authoritative.
      var setIsCustomised = isCustomised(set);
      var selectRowEl = node.querySelector('[data-fabric-select-row]');
      var styleSelect = node.querySelector('[data-fabric-style-select]');

      var sizeFieldEl = node.querySelector('[data-fabric-size-field]');
      if (sizeFieldEl) sizeFieldEl.hidden = setIsCustomised;
      if (window.PTSizeGuide) window.PTSizeGuide.snapshot(selectRowEl, set.gender, set.size, setIsCustomised);

      if (styleSelect) {
        styleSelect.setAttribute('data-set-id', set.id);
        if (set.gender) {
          populateSelect(styleSelect, {
            placeholder: 'Choose a Style',
            options: window.PTStyleCatalog.options(set.gender).map(function (s) {
              return { value: s.code, text: s.name, selected: set.style === s.code };
            })
          });
        } else {
          populateSelect(styleSelect, { placeholder: 'Select', options: [] });
        }
        if (window.PTStyleDropdown) window.PTStyleDropdown.enhance(styleSelect, set.gender);
      }

      /* -------------------------------------------------------------
         Customisation CTA / Customised panel (assets/pt-customisation-
         popup.js). The row is hidden entirely unless the popup module
         has loaded, the paid product is configured+available in Theme
         Settings, and this set's Gender has real catalogue data --
         never show an entry point that would silently fail to open.
         See PTCustomisation.getProductInfo()/isGenderReady().
      -------------------------------------------------------------- */
      var customiseRowEl = node.querySelector('[data-fabric-customise-row]');
      var customiseCtaEl = node.querySelector('[data-fabric-customise-row] > [data-fabric-customise-cta]');
      var customiseCtaPriceEl = customiseCtaEl ? customiseCtaEl.querySelector('[data-fabric-customise-cta-price]') : null;
      var customisedPanelEl = node.querySelector('[data-fabric-customised-panel]');
      var customisedDetailEl = node.querySelector('[data-fabric-customised-detail]');

      var custInfo = window.PTCustomisation ? window.PTCustomisation.getProductInfo() : { available: false };
      var custGenderReady = !!(set.gender && window.PTCustomisation && window.PTCustomisation.isGenderReady(set.gender));
      var custAvailable = !!(custInfo && custInfo.available) && custGenderReady;

      if (customiseRowEl) customiseRowEl.hidden = !custAvailable;
      if (customiseCtaEl) customiseCtaEl.hidden = setIsCustomised;
      if (customiseCtaPriceEl && custInfo.priceFormatted) {
        customiseCtaPriceEl.textContent = '+' + custInfo.priceFormatted;
      }
      if (customisedPanelEl) customisedPanelEl.hidden = !setIsCustomised;
      if (customisedDetailEl && setIsCustomised) {
        customisedDetailEl.textContent = custInfo.priceFormatted
          ? ('Custom size & fit · ' + custInfo.priceFormatted + ' per person')
          : 'Custom size & fit';
      }

      var confirmBtn = node.querySelector('[data-fabric-set-confirm]');
      if (confirmBtn) {
        confirmBtn.disabled = !info.complete;
        confirmBtn.textContent = 'Confirm Person';
      }
      var readyStatus = node.querySelector('[data-fabric-confirm-ready]');
      if (readyStatus) readyStatus.hidden = !info.complete;

      return node;
    }

    function renderCatalogBanner(progress) {
      if (!els.catalogError) return;
      if (progress.anyBlocked) {
        els.catalogError.textContent = 'Some sizes, styles or genders needed for one of your sets are not available in our catalog right now. Please contact us before ordering this configuration.';
        els.catalogError.hidden = false;
      } else {
        els.catalogError.hidden = true;
        els.catalogError.textContent = '';
      }
    }

    // `progress` is the same computeProgress() result render() already
    // computed for this pass -- reused here rather than recomputed, and
    // never stored as separate state.
    function renderSets(progress) {
      els.setsContainer.innerHTML = '';
      var removable = state.sets.length > MIN_SETS;

      state.sets.forEach(function (set, index) {
        els.setsContainer.appendChild(buildSetCard(set, index, removable));
      });

      if (els.addSetBtn) {
        // Progressive disclosure: offering a new blank set while an
        // existing one is still incomplete/unconfirmed would recreate
        // a long, multi-set form. Only ever available once every
        // active set is confirmed (or there simply are none yet).
        var allExistingConfirmed = progress.total === 0 || progress.confirmedCount === progress.total;
        els.addSetBtn.hidden = state.sets.length >= MAX_SETS || !allExistingConfirmed;
      }
    }

    function renderPricing(pricing) {
      if (!els.pricing) return;
      var show = pricing.count > 0;
      els.pricing.hidden = !show;
      if (!show) return;

      var solo = pricing.count === 1;
      els.pricing.classList.toggle('fabric-builder__pricing--solo', solo);
      if (els.pricingDetail) els.pricingDetail.hidden = solo;
      if (els.totalLabel) els.totalLabel.textContent = solo ? 'Your solo total' : 'Your set total';
      if (els.benefitNote) {
        els.benefitNote.hidden = false;
        els.benefitNote.textContent = solo
          ? 'Add another pajama set to unlock 10% Matching Benefit.'
          : pricing.tierPercent + '% Matching Benefit included.' + (pricing.count === 2 ? ' 3+ pajama sets get 15% Matching Benefit.' : '');
      }
      var originalPrice = root.querySelector('[data-fabric-original-price]');
      if (originalPrice) {
        originalPrice.hidden = !pricing.tierPercent;
        originalPrice.textContent = formatMoney(pricing.subtotalCents + pricing.serviceCents, data.moneyFormat);
      }
      var serviceRow = root.querySelector('[data-fabric-service-row]');
      if (serviceRow) {
        serviceRow.hidden = !pricing.serviceCents;
        serviceRow.querySelector('[data-fabric-service-price]').textContent = '+' + formatMoney(pricing.serviceCents, data.moneyFormat);
      }
      if (els.subtotal) els.subtotal.textContent = formatMoney(pricing.subtotalCents, data.moneyFormat);
      if (els.finalPrice) els.finalPrice.textContent = formatMoney(pricing.finalPriceCents, data.moneyFormat);
      var showBenefit = pricing.setBenefitCents > 0;
      if (els.setBenefitRow) els.setBenefitRow.hidden = !showBenefit;
      if (els.setBenefit && showBenefit) {
        els.setBenefit.textContent = '−' + formatMoney(pricing.setBenefitCents, data.moneyFormat);
      }
      if (els.benefitLabel) els.benefitLabel.textContent = 'Matching Benefit — ' + pricing.tierPercent + '%';
    }

    function renderSummary(progress) {
      if (els.summaryCount) {
        els.summaryCount.textContent = 'People confirmed: ' + progress.confirmedCount + ' of ' + progress.total;
      }

      renderPricing(computePricing());

      var allConfirmed = progress.total > 0 && progress.confirmedCount === progress.total && !progress.anyBlocked;
      state.allComplete = allConfirmed;

      if (els.summaryMessage) {
        els.summaryMessage.textContent = summaryMessageText(progress);
      }

      if (allConfirmed && !funnelConfiguredFired && window.PTFunnelTracking) {
        var funnelPricing = computePricing();
        var funnelConfiguredAccepted = window.PTFunnelTracking.fire('ProductConfigured', funnelTrackingParams({
          set_count: progress.total,
          value: (typeof funnelPricing.finalPriceCents === 'number')
            ? (funnelPricing.finalPriceCents / 100)
            : undefined
        }));
        if (funnelConfiguredAccepted) funnelConfiguredFired = true;
      }

      if (els.summary) {
        els.summary.classList.toggle('fabric-builder__summary--ready', allConfirmed);
      }

      if (els.cta) {
        if (state.submitting) {
          els.cta.disabled = true;
          els.cta.setAttribute('aria-busy', 'true');
          els.cta.textContent = editMode ? 'Updating…' : 'Adding…';
        } else {
          els.cta.disabled = !allConfirmed || editBlocked;
          els.cta.removeAttribute('aria-busy');
          els.cta.textContent = editMode ? 'UPDATE CART' : 'Add to Cart';
        }
      }
    }

    // Full structural render -- rebuilds every set card's DOM. Safe
    // to call on any click-driven structural change (add/remove/
    // confirm/expand/collapse/gender/size); never called directly
    // from a Style click or a measurement keystroke handler (see
    // refreshLight() and the file header comment).
    function render() {
      var progress = computeProgress();
      renderCatalogBanner(progress);
      renderSets(progress);
      renderSummary(progress);
    }

    // Lightweight update path for Style selection: touches only the
    // summary/pricing/CTA and the one affected set card's completion
    // class/error tag/Confirm-button state in place, without
    // rebuilding any card's DOM -- so the Style select never loses
    // focus or its native open state.
    function refreshLight(setId) {
      var progress = computeProgress();
      renderCatalogBanner(progress);
      renderSummary(progress);

      var node = els.setsContainer.querySelector('[data-set-id="' + setId + '"]');
      var set = findSet(setId);
      if (!node || !set) return;

      var info = setCompletionInfo(set);
      node.classList.toggle('fabric-builder__set--complete', info.complete);
      node.classList.toggle('fabric-builder__set--confirmed', !!set.confirmed);

      var errorEl = node.querySelector('[data-fabric-set-error]');
      if (errorEl) {
        if (info.blockedByCatalog) {
          errorEl.textContent = 'Unavailable';
          errorEl.hidden = false;
        } else {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }
      }

      var confirmBtn = node.querySelector('[data-fabric-set-confirm]');
      if (confirmBtn) {
        confirmBtn.disabled = !info.complete;
        confirmBtn.textContent = 'Confirm Person';
      }
      var readyStatus = node.querySelector('[data-fabric-confirm-ready]');
      if (readyStatus) readyStatus.hidden = !info.complete;
    }

    /* ---------------------------------------------------------------
       Add to Cart -- Matching's final multi-item submission, adapted:
       one item per set using its real Gender variant id, public
       Size/Style properties, and private _set_id/_person for
       duplicate-line protection.

       Phase 7.3 change: the readiness gate is "every active set is
       confirmed" (not merely complete) -- confirming a set already
       requires it to be complete, and a confirmed set can only be
       un-confirmed by a real edit, so this is both simpler and
       stricter than the Phase 7.2 gate.
    ------------------------------------------------------------------ */
    function generateSetSubmissionId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      var randomPart = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
      return 'fset-' + Date.now().toString(36) + '-' + randomPart;
    }

    function setCartError(message) {
      if (!els.cartError) return;
      if (message) {
        els.cartError.textContent = message;
        els.cartError.hidden = false;
      } else {
        els.cartError.textContent = '';
        els.cartError.hidden = true;
      }
    }

    function scrollSetIntoView(setId) {
      var node = els.setsContainer.querySelector('[data-set-id="' + setId + '"]');
      if (!node) return;
      if (typeof node.scrollIntoView === 'function') {
        // "nearest" only scrolls when the card isn't already
        // sufficiently in view -- a gentle nudge, not a disruptive
        // jump to the center of the viewport every time.
        node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    function focusSet(setId) {
      expandSet(setId, false);
      render();
      scrollSetIntoView(setId);
      var node = els.setsContainer.querySelector('[data-set-id="' + setId + '"]');
      if (node && typeof node.focus === 'function') node.focus();
    }

    function handleAddToCart() {
      if (state.submitting || editBlocked) return;

      // Re-validate at click time rather than trusting the disabled
      // attribute alone. Also names and expands/scrolls to the first
      // set that still needs attention -- either genuinely incomplete,
      // or complete but not yet confirmed.
      var progress = computeProgress();
      var allConfirmed = progress.total > 0 && progress.confirmedCount === progress.total && !progress.anyBlocked;
      if (!allConfirmed) {
        if (progress.incompleteSets.length > 0) {
          var first = progress.incompleteSets[0];
          if (els.summaryMessage) {
            els.summaryMessage.textContent = 'Person ' + (first.index + 1) + ' needs ' + first.info.missing.join(', ');
          }
          focusSet(first.set.id);
        } else if (progress.unconfirmedCompleteSets.length > 0) {
          var readySet = progress.unconfirmedCompleteSets[0];
          if (els.summaryMessage) {
            els.summaryMessage.textContent = 'Person ' + (readySet.index + 1) + ' is ready — tap Confirm Person';
          }
          focusSet(readySet.set.id);
        }
        return;
      }

      // A Customised set needs the Customisation product/variant to
      // still be resolvable at submit time -- catalog data (Gender/
      // Size) is re-checked above via computeProgress()/setCompletionInfo(),
      // but the Customisation product is a separate, merchant-managed
      // Theme Setting (assets/pt-customisation-popup.js) that could in
      // principle change between page load and this click. Fail safely
      // rather than submit a line with no real variant id.
      var anyCustomisedSet = state.sets.some(function (s) { return isCustomised(s); });
      var custProductInfo = window.PTCustomisation ? window.PTCustomisation.getProductInfo() : { available: false };
      if (anyCustomisedSet && (!custProductInfo || !custProductInfo.available)) {
        setCartError('The Customisation service is temporarily unavailable. Please remove Customisation from your set(s) or try again shortly.');
        return;
      }

      var setSubmissionId = editMode ? window.PTCartEdit.id : generateSetSubmissionId();

      // Phase 11C.1 (corrected): no discount tier is submitted here --
      // the Matching Benefit is a cart-wide calculation across all
      // qualifying merchandise (which may come from other PDPs added
      // separately), not something one builder submission can know.
      // That is Phase 11C.2's job. Unaffected by Customisation: the
      // ₹500 Customisation line is separate merchandise with its own
      // price, not a discount input.
      var items = [];
      state.sets.forEach(function (set) {
        var garmentProperties = {
          _set_id: setSubmissionId,
          _person: set.id,
          Person: { WOMEN: 'Woman', MEN: 'Man', GIRLS: 'Girl', BOYS: 'Boy' }[set.gender]
        };

        garmentProperties.Style = window.PTStyleCatalog.cartValue(set.gender, set.style);
        garmentProperties._style_code = set.style;

        if (isCustomised(set)) {
          // A private id shared by this pair (garment + Customisation
          // line) purely for order-data correlation -- it is NOT what
          // makes the Customisation line unique in the cart (see
          // below); that is its own job, per the task instruction not
          // to reuse _set_id/_person as the Customisation identity.
          var customisationId = set.cartCustomisationId || window.PTCustomisation.generateId();
          if (editMode) set.cartCustomisationId = customisationId;
          var draft = set.customisation;

          garmentProperties.Customisation = 'Yes — see the linked Customisation line';
          garmentProperties._customisation_id = customisationId;

          var custProperties = {
            Profile: draft.profileName || ('Set ' + (state.sets.indexOf(set) + 1)),
            Gender: GENDER_TEXT[set.gender],
            'Measurements Later': draft.measurementsLater ? 'Yes' : 'No',
            _customisation_id: customisationId
          };
          if (draft.topSize) custProperties['Top Size'] = draft.topSize;
          if (draft.bottomSize) custProperties['Bottom Size'] = draft.bottomSize;
          custProperties.Style = window.PTStyleCatalog.label(set.gender, set.style);
          custProperties._style_code = set.style;
          if (draft.chest) custProperties.Chest = draft.chest + ' in';
          if (draft.waist) custProperties.Waist = draft.waist + ' in';
          if (draft.hips) custProperties.Hips = draft.hips + ' in';
          if (draft.height) custProperties.Height = draft.height;
          if (draft.notes) custProperties.Notes = draft.notes;

          items.push({
            id: variantIdForGender(set.gender),
            quantity: 1,
            properties: garmentProperties
          });
          items.push({
            id: custProductInfo.variantId,
            quantity: 1,
            properties: custProperties
          });
        } else {
          garmentProperties.Size = set.size;
          items.push({
            id: variantIdForGender(set.gender),
            quantity: 1,
            properties: garmentProperties
          });
        }
      });

      if (items.some(function (item) { return !item.id; })) {
        setCartError('Something went wrong preparing your sets. Please refresh the page and try again.');
        return;
      }

      setCartError(null);
      state.submitting = true;
      render();

      var submission = editMode
        ? window.PTCartEdit.save(editSession, items)
        : fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) {
          return response.json()
            .catch(function () { return null; })
            .then(function (json) { return { ok: response.ok, json: json }; });
        });
      submission
        .then(function (result) {
          if (!result.ok) {
            var errorMessage = 'We could not add your sets to the cart. Please try again.';
            if (result.json) {
              if (typeof result.json.description === 'string' && result.json.description) {
                errorMessage = result.json.description;
              } else if (typeof result.json.message === 'string' && result.json.message) {
                errorMessage = result.json.message;
              }
            }
            throw new Error(errorMessage);
          }
          if (!result.json || !Array.isArray(result.json.items)) {
            throw new Error('We could not add your sets to the cart. Please try again.');
          }

          state.submitting = false;
          render();

          // Native Symmetry 8.3.2 mechanism -- same events Matching's
          // Phase 4 assessment confirmed refresh both the header cart
          // count and the cart-drawer's own nested cart-form, then
          // open the drawer. No manual DOM/count updates.
          document.dispatchEvent(new CustomEvent('on:cart:change', {
            bubbles: true,
            cancelable: false
          }));
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
            bubbles: true,
            cancelable: false,
            detail: { opener: els.cta }
          }));
        })
        .catch(function (error) {
          if (editMode) editBlocked = true;
          state.submitting = false;
          setCartError(error && error.message ? error.message : 'We could not add your sets to the cart. Please try again.');
          render();
          if (editMode) window.PTCartEdit.showRecovery(els.cartError);
          // eslint-disable-next-line no-console
          console.log(error);
        });
    }

    /* ---------------------------------------------------------------
       Event delegation.
    ------------------------------------------------------------------ */
    function onChange(event) {
      if (editMode && (editBlocked || state.submitting)) return;
      var target = event.target;
      if (!target || !target.matches) return;

      if (target.matches('[data-fabric-field]')) {
        var field = target.getAttribute('data-fabric-field');
        var setId = target.getAttribute('data-set-id');
        if (field === 'gender') requestGenderChange(setId, target.value);
        else if (field === 'size') setSize(setId, target.value);
        else if (field === 'style') setStyle(setId, target.value);
      }
    }

    function onClick(event) {
      if (editMode && (editBlocked || state.submitting)) return;
      var addBtn = event.target.closest && event.target.closest('[data-fabric-add-set]');
      if (addBtn) {
        addSet();
        return;
      }

      var confirmBtn = event.target.closest && event.target.closest('[data-fabric-set-confirm]');
      if (confirmBtn && !confirmBtn.disabled) {
        var confirmSetEl = confirmBtn.closest('[data-fabric-set]');
        var confirmSetId = confirmSetEl ? confirmSetEl.getAttribute('data-set-id') : null;
        if (confirmSetId) confirmSet(confirmSetId);
        return;
      }

      var genderYesBtn = event.target.closest && event.target.closest('[data-fabric-gender-confirm-yes]');
      if (genderYesBtn) {
        var yesSetEl = genderYesBtn.closest('[data-fabric-set]');
        var yesSetId = yesSetEl ? yesSetEl.getAttribute('data-set-id') : null;
        if (yesSetId) confirmGenderChange(yesSetId);
        return;
      }

      var genderNoBtn = event.target.closest && event.target.closest('[data-fabric-gender-confirm-no]');
      if (genderNoBtn) {
        var noSetEl = genderNoBtn.closest('[data-fabric-set]');
        var noSetId = noSetEl ? noSetEl.getAttribute('data-set-id') : null;
        if (noSetId) cancelGenderChange(noSetId);
        return;
      }

      // Same button serves both "open Customisation" (not yet
      // Customised) and "Edit Customisation" (already Customised) --
      // both are a plain open() call with whatever draft already
      // exists (see draftForPopup()); only the popup's own explicit
      // "CUSTOMISE THIS SET" click (its onSave callback, wired here)
      // ever changes this set's state. See assets/pt-customisation-
      // popup.js for the exploration-only guarantee.
      var customiseBtn = event.target.closest && event.target.closest('[data-fabric-customise-cta]');
      if (customiseBtn) {
        var customiseSetEl = customiseBtn.closest('[data-fabric-set]');
        var customiseSetId = customiseSetEl ? customiseSetEl.getAttribute('data-set-id') : null;
        var customiseSet = customiseSetId ? findSet(customiseSetId) : null;
        if (customiseSet && customiseSet.gender && window.PTCustomisation) {
          window.PTCustomisation.open({
            gender: customiseSet.gender,
            initialDraft: draftForPopup(customiseSet),
            triggerEl: customiseBtn,
            onSave: function (draft) { saveCustomisation(customiseSetId, draft); }
          });
        }
        return;
      }

      var useStandardBtn = event.target.closest && event.target.closest('[data-fabric-use-standard]');
      if (useStandardBtn) {
        var standardSetEl = useStandardBtn.closest('[data-fabric-set]');
        var standardSetId = standardSetEl ? standardSetEl.getAttribute('data-set-id') : null;
        if (standardSetId) useStandardOptions(standardSetId);
        return;
      }

      // [data-fabric-set-edit-hint] now lives alongside Remove (grid-
      // aligned to it, see CSS) rather than nested inside the header
      // toggle button -- browsers do not allow nesting one button
      // inside another. It routes through the identical expand logic
      // below via this shared lookup, so tapping the visible "Edit"
      // text still works exactly as when it was part of the button.
      var headerToggle = event.target.closest && event.target.closest('[data-fabric-set-header], [data-fabric-set-edit-hint]');
      if (headerToggle) {
        var headerSetEl = headerToggle.closest('[data-fabric-set]');
        var headerSetId = headerSetEl ? headerSetEl.getAttribute('data-set-id') : null;
        var headerSet = headerSetId ? findSet(headerSetId) : null;
        if (headerSet) {
          if (!headerSet.expanded) {
            expandSet(headerSetId, false);
            render();
            scrollSetIntoView(headerSetId);
          } else if (headerSet.confirmed) {
            headerSet.expanded = false;
            render();
          }
        }
        return;
      }

      var removeBtn = event.target.closest && event.target.closest('[data-fabric-set-remove]');
      if (removeBtn && !removeBtn.hidden) {
        var setEl = removeBtn.closest('[data-fabric-set]');
        var setId = setEl ? setEl.getAttribute('data-set-id') : null;
        var set = setId ? findSet(setId) : null;
        if (!set) return;

        if (set.confirmed && !set.pendingRemove) {
          set.pendingRemove = true;
          set.removeArmTimer = setTimeout(function () {
            set.pendingRemove = false;
            set.removeArmTimer = null;
            render();
          }, REMOVE_ARM_TIMEOUT_MS);
          render();
          return;
        }

        removeSet(setId);
        return;
      }

      var cartBtn = event.target.closest && event.target.closest('[data-fabric-add-to-cart]');
      if (cartBtn && !cartBtn.disabled) {
        handleAddToCart();
      }
    }

    root.addEventListener('change', onChange);
    root.addEventListener('click', onClick);

    if (editMode) {
      render();
      setCartError('Loading your saved configuration…');
      if (!window.PTCartEdit || !window.PTCartEdit.id) {
        setCartError('This edit link cannot be loaded. Your cart is unchanged.');
        return;
      }
      window.PTCartEdit.load(data, 'fabric', sizesForGender).then(function (session) {
        editSession = session;
        if (session.people.length > data.maxSets) throw new Error('This group exceeds the current builder limit. Your cart is unchanged.');
        state.sets = session.people.map(function (person) { return Object.assign(buildSet(), person); });
        setCounter = Math.max(setCounter, ...session.people.map(function (person) { return Number(person.id.split('-').pop()); }));
        editBlocked = false;
        setCartError(null);
        window.PTCartEdit.showNotice(els.setsContainer, session.people.some(function (person) { return !person.confirmed; })
          ? 'We restored your saved selections. Review the open cards, then select UPDATE CART to replace this set.' : null);
        render();
      }).catch(function (error) {
        setCartError(error.message || 'The configuration could not be loaded. Your cart is unchanged.');
        render();
        // Offer rebuilding only after cart ownership and saved details passed
        // the shared audit. Keep the same update target and original snapshot.
        window.PTCartEdit.showRecovery(els.cartError, editSession ? function () {
          state.sets = [buildSet()];
          editBlocked = false;
          setCartError(null);
          window.PTCartEdit.showNotice(els.setsContainer, 'Choose your configuration again. UPDATE CART will replace the existing set; your cart stays unchanged until you save.');
          render();
        } : null);
      });
      return;
    }

    // Set 1 exists from the start, expanded -- there is no
    // relationship-selector gate here, unlike Matching.
    var firstSet = buildSet();
    expandStampCounter += 1;
    firstSet.expandStamp = expandStampCounter;
    state.sets.push(firstSet);
    render();
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-fabric-builder]');
    for (var i = 0; i < roots.length; i++) {
      init(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme Editor re-renders a section's DOM on save without a full
  // page reload -- same re-init safeguard Matching's final JS carries
  // (init() is idempotent via the data-fabric-initialized guard).
  document.addEventListener('shopify:section:load', initAll);
})();
